import { useEffect, useState } from "react";
import {
  deleteBroadcastGroupData,
  getBroadcastGroupsData,
  putBroadcastGroupData,
} from "@services/API/Requests/BroadcastGroupRequests";

import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import { error_message } from "@constants/Errors";
import { TranslateErrorMessage } from "@services/ErrorHandling";
import { BROADCASTGROUP } from "@types/broadcastGroup";

export const UseYobelyMainPage = () => {
  const navigate = useNavigate();
  const [broadcastGroups, setBroadcastGroups] = useState<BROADCASTGROUP[]>([]);
  const [expandedNodes, setExpandedNodes] = useState<{
    [key: string]: boolean;
  }>({});

  const [error, setError] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const errorToast = (response) => toast.error(`${response}`);
  const successToast = (response) => toast.success(`${response}`);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const warningModalTitle: string = "一斉発信グループを削除";
  const warningModalBody: string =
    "一斉発信グループを削除します。\nこの操作により、関連するすべての子部署も削除されます。\nよろしいですか？";
  const warningModalConfirm: string = "削除";

  const [parentModalOpen, setParentModalOpen] = useState<boolean>(false);

  const [selectedNodeId, setSelectedNodeId] = useState<number>(0);

  const updateErrorToast = (error) => toast.error(`${error}`);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalGroups, setTotalGroups] = useState<number>(0);
  const [itemsPerPage, setItemsPerPage] = useState<number>(5);

  const totalPages: number = Math.ceil(totalGroups / itemsPerPage);

  const toggleExpand = (id) => {
    setExpandedNodes((prevState) => ({
      ...prevState,
      [id]: !prevState[id],
    }));
  };

  const fetchData = async (
    curPage = currentPage,
    itemPerPage = itemsPerPage,
  ) => {
    try {
      setLoading(true);
      await getBroadcastGroupsData(
        setBroadcastGroups,
        setLoading,
        setError,
        curPage,
        itemPerPage,
        setTotalGroups,
      );
    } catch (err) {
      setError(error_message.departments.load_failed);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const buildGroupTree: (departments: any) => never[] = (departments) => {
    const sortedDepartments: any = departments.sort((a, b) =>
      a.code.localeCompare(b.code),
    );

    const map = {};
    const roots = [];

    sortedDepartments.forEach((dept) => {
      map[dept.id] = { ...dept, children: [] };
    });

    sortedDepartments.forEach((dept) => {
      if (dept.parent === null) {
        roots.push(map[dept.id]);
      } else {
        if (map[dept.parent]) {
          map[dept.parent].children.push(map[dept.id]);
        }
      }
    });

    const sortChildren = (nodes) => {
      nodes.forEach((node) => {
        if (node.children.length > 0) {
          node.children.sort((a, b) => a.code.localeCompare(b.code));
          sortChildren(node.children);
        }
      });
    };

    sortChildren(roots);

    return roots;
  };

  const handleOpenEditPage = (index) => {
    navigate(`/YOBELY/broadcast_groups/${index}`);
  };

  const handleOpenCreatePage = (index) => {
    navigate(`/YOBELY/broadcast_groups/create/${index}`);
  };

  const handleOpenChangeParentModal = (nodeId) => {
    setSelectedNodeId(nodeId);
    setParentModalOpen(true);
  };

  const handleCloseParentModal = () => {
    setParentModalOpen(false);
  };

  const handleConfirmParentModal = (id, newParent) => {
    setParentModalOpen(false);

    const departmentIndex: number = broadcastGroups.findIndex(
      (dept) => dept.id === id,
    );

    if (departmentIndex !== -1) {
      const updatedDepartments = [...broadcastGroups];

      updatedDepartments[departmentIndex] = {
        ...updatedDepartments[departmentIndex],
        parent: newParent,
      };

      setBroadcastGroups(updatedDepartments);

      putBroadcastGroupData(
        updatedDepartments[departmentIndex],
        setLoading,
        setError,
      )
        .then((response) => {
          if (response.status >= 300) {
            updateErrorToast(TranslateErrorMessage(response.message));
            return;
          }
          fetchData();
        })
        .catch((err) => {
          console.error(error_message.departments.parent_failed, err);
          errorToast(err.message);
        });
    } else {
      errorToast(error_message.departments.not_found);
      console.error(error_message.departments.not_found);
    }
  };

  const handleDelete = async (event) => {
    setDeleteModalOpen(false);
    event.preventDefault();

    await deleteBroadcastGroupData(selectedNodeId, setLoading, setError)
      .then((response) => {
        if (response.status == 200) {
          successToast(response.message);
          fetchData();
        } else {
          const errorMessage = TranslateErrorMessage(response.message);
          setError(errorMessage);
          errorToast(errorMessage);
        }
      })
      .catch((error) => {
        const errorMessage = TranslateErrorMessage(error.message);
        setError(errorMessage);
        errorToast(errorMessage);
      });

    onCancel();
  };

  const onCancel = () => {
    handleCloseDeleteModal();
  };

  const handleOpenDeleteModal = (node_id) => {
    setSelectedNodeId(node_id);
    setDeleteModalOpen(true);
  };

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false);
  };

  return {
    loading,
    expandedNodes,
    broadcastGroups,
    warningModalBody,
    warningModalTitle,
    warningModalConfirm,
    deleteModalOpen,
    parentModalOpen,
    selectedNodeId,
    handleDelete,
    handleCloseParentModal,
    handleConfirmParentModal,
    handleCloseDeleteModal,
    toggleExpand,
    handleOpenChangeParentModal,
    handleOpenDeleteModal,
    handleOpenCreatePage,
    handleOpenEditPage,
    buildGroupTree,
  };
};
